import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styles from './Tooltip.module.scss';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  hideDelay?: number;
  offset?: number;
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  placement = 'top',
  delay = 200,
  hideDelay = 300,
  offset = 10,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimeouts = () => {
    if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  };

  const showTooltip = useCallback(() => {
    clearTimeouts();
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  }, [delay]);

  const hideTooltip = useCallback(() => {
    clearTimeouts();
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, hideDelay);
  }, [hideDelay]);

  useEffect(() => {
    return () => clearTimeouts();
  }, []);

  const getTooltipPosition = () => {
    if (!triggerRef.current) return {};
    const triggerRect = triggerRef.current.getBoundingClientRect();
    
    // Defer measurement until the tooltip is actually rendered
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();

    let top = 0, left = 0;

    switch (placement) {
      case 'top':
        top = triggerRect.top - (tooltipRect?.height || 0) - offset;
        left = triggerRect.left + (triggerRect.width / 2) - ((tooltipRect?.width || 0) / 2);
        break;
      case 'bottom':
        top = triggerRect.bottom + offset;
        left = triggerRect.left + (triggerRect.width / 2) - ((tooltipRect?.width || 0) / 2);
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height / 2) - ((tooltipRect?.height || 0) / 2);
        left = triggerRect.left - (tooltipRect?.width || 0) - offset;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height / 2) - ((tooltipRect?.height || 0) / 2);
        left = triggerRect.right + offset;
        break;
    }
    return { top: `${top}px`, left: `${left}px` };
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={className}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        style={{ display: 'inline-block' }} 
      >
        {children}
      </div>
      {isVisible && ReactDOM.createPortal(
        <div
          ref={tooltipRef}
          className={`${styles.tooltip} ${styles.tooltipVisible}`}
          style={getTooltipPosition()}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <div className={`${styles.arrow} ${styles[`arrow-${placement}`]}`} />
          {content}
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip; 